import {OnSuccessData} from "../store/action/actionHelpers";
import {none, option, Option} from "ts-option";

const exif = require('exif-parser');

export interface ImgFileDimensions {
	/**
	 * Maximum image height
	 */
	maxHeight: number
}

export default class ImageUtils {
	/**
	 * Jpeg image orientation fix based on exif tag value.
	 * @param {Buffer} srcBase64
	 * @param {OnSuccessData<string>} onSuccess
	 */
	public static resetOrientation(srcBase64: Buffer, onSuccess: OnSuccessData<string>) {
		const parser = exif.create(srcBase64, window);
		const result = parser.parse();
		const srcOrientation = result.tags.Orientation;

		let img = new Image();
		img.src = 'data:image/jpeg;base64,' + srcBase64.toString('base64');

		img.onload = () => {
			const width = img.width,
				height = img.height,
				canvas = document.createElement('canvas'),
				ctx = canvas.getContext("2d");

			if (4 < srcOrientation && srcOrientation < 9) {
				canvas.width = height;
				canvas.height = width;
			}
			else {
				canvas.width = width;
				canvas.height = height;
			}

			switch (srcOrientation) {
				case 2:
					ctx.translate(width, 0);
					ctx.scale(-1, 1);
					break;
				case 3:
					ctx.translate(width, height);
					ctx.rotate(Math.PI);
					break;
				case 4:
					ctx.translate(0, height);
					ctx.scale(1, -1);
					break;
				case 5:
					ctx.rotate(0.5 * Math.PI);
					ctx.scale(1, -1);
					break;
				case 6:
					ctx.rotate(0.5 * Math.PI);
					ctx.translate(0, -height);
					break;
				case 7:
					ctx.rotate(0.5 * Math.PI);
					ctx.translate(width, -height);
					ctx.scale(-1, 1);
					break;
				case 8:
					ctx.rotate(-0.5 * Math.PI);
					ctx.translate(-width, 0);
					break;
				default:
					break;
			}
			ctx.drawImage(img, 0, 0);
			onSuccess(canvas.toDataURL());
		}
	}

	public static getImageAsSrcString(srcBase64: Buffer, onSuccess: OnSuccessData<string>) {
		let img = new Image();
		img.src = 'data:image/png;base64,' + srcBase64.toString('base64');

		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext("2d");
			ctx.drawImage(img, 0, 0);
			onSuccess(canvas.toDataURL());
		}
	}

	public static rotate(degrees: number, srcBase64: string, onSuccess: OnSuccessData<string>) {
		let img = new Image();
		img.src = srcBase64;

		img.onload = () => {
			const width = img.width,
				height = img.height,
				canvas = document.createElement('canvas'),
				ctx = canvas.getContext("2d");
			canvas.width = height;
			canvas.height = width;

			ctx.clearRect(0, 0, canvas.width, canvas.height);
			// save the unrotated context of the canvas so we can restore it later
			ctx.save();
			// move to the center of the canvas
			ctx.translate(canvas.width / 2, canvas.height / 2);
			// rotate the canvas to the specified degrees
			ctx.rotate(degrees * Math.PI / 180);
			// draw the image
			ctx.drawImage(img, -img.width / 2, -img.height / 2);
			// we’re done with the rotating so restore the unrotated context
			ctx.restore();

			onSuccess(canvas.toDataURL());
		}
	}

	public static checkDimensions(imgUrl: string, dimensions: ImgFileDimensions): Promise<Option<string[]>> {
		return new Promise((resolve, reject) => {
			let newImage = new Image();
			newImage.src = imgUrl;
			newImage.onload = () => {
				if (dimensions.maxHeight && (newImage.naturalHeight > dimensions.maxHeight)) {
					const errors = [`The image height is incorrect. Max image height is ${dimensions.maxHeight}px.`];
					reject(option(errors));
				}
				else {
					resolve(none);
				}
			}
		});
	}
}